import React, { useState, Suspense, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Button, Dialog, DialogContent, DialogActions } from '@mui/material';
import { LockPersonOutlined, DataObject, TextFields, Fullscreen, Close } from "@mui/icons-material";
import { backgroundColor, colorText, colorTextInactive, monacoTheme } from "../../App/theme.js";
import { isMobile, isMac } from "../../App/utils.js";

const LazyEditor = React.lazy(() => import('@monaco-editor/react'));

export const JSONField = ({ value, onChange, attr, disabled }) => {
    const [editorMode, setEditorMode] = useState('json');
    const [jsonError, setJsonError] = useState(null);
    const [localValue, setLocalValue] = useState('');
    const [isValidJSON, setIsValidJSON] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenValue, setFullscreenValue] = useState('');

    // Auto-detect if content is JSON
    const detectContentType = (content) => {
        if (!content || typeof content !== 'string') return 'text';
        
        const trimmed = content.trim();
        if (!trimmed) return 'text';
        
        // Check if it starts with JSON-like characters
        if (trimmed[0] === '{' || trimmed[0] === '[' || trimmed[0] === '"') {
            try {
                JSON.parse(trimmed);
                return 'json';
            } catch {
                return 'text';
            }
        }
        return 'text';
    };

    useEffect(() => {
        if (value) {
            const detectedType = detectContentType(value);
            setEditorMode(detectedType);
            
            if (detectedType === 'json') {
                try {
                    const parsed = typeof value === 'object' ? value : JSON.parse(value);
                    const formatted = JSON.stringify(parsed, null, 2);
                    setLocalValue(formatted);
                    setJsonError(null);
                    setIsValidJSON(true);
                } catch (error) {
                    setLocalValue(value);
                    setJsonError('Invalid JSON format');
                    setIsValidJSON(false);
                }
            } else {
                setLocalValue(value);
                setIsValidJSON(false);
            }
        } else {
            setLocalValue('');
            setIsValidJSON(false);
        }
    }, [value]);

    const handleChange = (newValue) => {
        setLocalValue(newValue);
        
        if (editorMode === 'json' && newValue.length >= 10) {
            try {
                JSON.parse(newValue);
                setJsonError(null);
                setIsValidJSON(true);
            } catch (error) {
                setJsonError(error.message);
                setIsValidJSON(false);
            }
        } else if (editorMode === 'json' && newValue.length < 10) {
            // Don't validate JSON for short strings to allow starting to type
            setJsonError(null);
            setIsValidJSON(false);
        }
        
        // Auto-save on change
        onChange(newValue, attr);
    };
    
    const handleOpenFullscreen = () => {
        setFullscreenValue(localValue);
        setIsFullscreen(true);
    };
    
    const handleCloseFullscreen = () => {
        setIsFullscreen(false);
    };
    
    const handleSaveFullscreen = () => {
        setLocalValue(fullscreenValue);
        onChange(fullscreenValue, attr);
        setIsFullscreen(false);
    };
    
    const handleFullscreenChange = (newValue) => {
        setFullscreenValue(newValue);
        if (editorMode === 'json' && newValue.length >= 10) {
            try {
                JSON.parse(newValue);
                setJsonError(null);
            } catch (error) {
                setJsonError(error.message);
            }
        }
    };

    const formatJSON = () => {
        try {
            const parsed = JSON.parse(localValue);
            const formatted = JSON.stringify(parsed, null, 2);
            setLocalValue(formatted);
            setJsonError(null);
            onChange(formatted, attr);  // Save formatted JSON
        } catch (error) {
            setJsonError('Unable to format: ' + error.message);
        }
    };

    const labelBody = (
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center">
                {attr?.encrypted && <LockPersonOutlined style={{ marginRight: 8, fontSize: 18, color: colorText }} />}
                <Typography variant="body2" style={{ color: colorText }}>
                    {attr?.label || attr?.attrName || 'JSON Data'}
                </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
                {jsonError && (
                    <Typography variant="caption" style={{ color: '#ff5252', marginRight: 8 }}>
                        {jsonError}
                    </Typography>
                )}
                <Tooltip title={editorMode === 'json' ? 'Switch to Text Mode' : 'Switch to JSON Mode'}>
                    <IconButton
                        size="small"
                        onClick={() => {
                            const newMode = editorMode === 'json' ? 'text' : 'json';
                            setEditorMode(newMode);
                            if (newMode === 'json') {
                                try {
                                    JSON.parse(localValue);
                                    setJsonError(null);
                                    setIsValidJSON(true);
                                } catch (error) {
                                    setJsonError('Content is not valid JSON');
                                    setIsValidJSON(false);
                                }
                            } else {
                                setJsonError(null);
                            }
                        }}
                        disabled={disabled}
                        style={{ 
                            padding: 4,
                            color: editorMode === 'json' ? colorText : colorTextInactive
                        }}
                    >
                        {editorMode === 'json' ? <DataObject fontSize="small" /> : <TextFields fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Fullscreen Editor">
                    <IconButton
                        size="small"
                        onClick={handleOpenFullscreen}
                        disabled={disabled}
                        style={{ padding: 4 }}
                    >
                        <Fullscreen fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ width: '100%', border: '1px solid rgba(255, 255, 255, 0.12)', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.02)', 
                padding: '8px 12px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.12)'
            }}>
                {labelBody}
                {!isMobile && (
                    <Typography variant="caption" sx={{ 
                        display: 'block',
                        mt: 0.5,
                        color: colorTextInactive,
                        fontSize: '0.65rem'
                    }}>
                        {isMac ? '⌘S' : 'Ctrl+S'} auto-saves
                    </Typography>
                )}
            </Box>
            
            <Suspense fallback={<Box p={2}>Loading editor...</Box>}>
                <LazyEditor
                    options={{
                        readOnly: disabled,
                        minimap: { enabled: false },
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        formatOnPaste: true,
                        formatOnType: true,
                        folding: true,
                        wordWrap: 'on',
                        fontSize: 13,
                        tabSize: 2,
                        scrollbar: {
                            vertical: 'auto',
                            horizontal: 'auto',
                            verticalScrollbarSize: 10,
                            horizontalScrollbarSize: 10
                        },
                        padding: {
                            top: 10,
                            bottom: 10
                        }
                    }}
                    height="400px"
                    language={editorMode}
                    value={localValue}
                    onChange={handleChange}
                    theme={`custom-${monacoTheme}`}
                    beforeMount={(monaco) => {
                        monaco.editor.defineTheme(`custom-${monacoTheme}`, {
                            base: monacoTheme,
                            inherit: true,
                            rules: [],
                            colors: {
                                'editor.background': backgroundColor,
                                'editorLineNumber.foreground': colorTextInactive,
                                'editorLineNumber.activeForeground': colorText,
                            }
                        });

                        if (editorMode === 'json') {
                            monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                                validate: true,
                                allowComments: false,
                                schemas: [],
                                schemaValidation: 'error',
                                schemaRequest: 'error',
                                trailingCommas: 'error',
                                comments: 'error'
                            });
                        }
                    }}
                    onMount={(editor) => {
                        editor.updateOptions({
                            contextmenu: true,
                            smoothScrolling: true,
                            cursorBlinking: 'smooth',
                            cursorSmoothCaretAnimation: true,
                            renderWhitespace: 'selection',
                            renderControlCharacters: false,
                            fontLigatures: true,
                            bracketPairColorization: {
                                enabled: true,
                                independentColorPoolPerBracketType: true
                            }
                        });
                    }}
                />
            </Suspense>
            
            {attr?.placeholder && !localValue && (
                <Box p={2} style={{ color: colorTextInactive }}>
                    <Typography variant="caption">{attr.placeholder}</Typography>
                </Box>
            )}
            
            {/* Fullscreen Dialog */}
            <Dialog
                open={isFullscreen}
                onClose={handleCloseFullscreen}
                maxWidth={false}
                fullScreen
                PaperProps={{
                    style: {
                        backgroundColor: backgroundColor,
                    }
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100vh',
                    backgroundColor: backgroundColor
                }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 20px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                    }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" style={{ color: colorText }}>
                                {attr?.label || attr?.attrName || 'JSON Editor'} - Fullscreen
                            </Typography>
                            {jsonError && (
                                <Typography variant="caption" style={{ color: '#ff5252' }}>
                                    {jsonError}
                                </Typography>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            {!isMobile && (
                                <Typography variant="caption" style={{ color: colorTextInactive, fontSize: '0.75rem', marginRight: 8 }}>
                                    {isMac ? '⌘S' : 'Ctrl+S'} auto-saves
                                </Typography>
                            )}
                            <Tooltip title={editorMode === 'json' ? 'Switch to Text Mode' : 'Switch to JSON Mode'}>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const newMode = editorMode === 'json' ? 'text' : 'json';
                                        setEditorMode(newMode);
                                    }}
                                    style={{ color: editorMode === 'json' ? colorText : colorTextInactive }}
                                >
                                    {editorMode === 'json' ? <DataObject /> : <TextFields />}
                                </IconButton>
                            </Tooltip>
                            <IconButton 
                                onClick={handleSaveFullscreen}
                                style={{ color: colorText }}
                            >
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                    
                    {/* Editor Content */}
                    <DialogContent sx={{ flexGrow: 1, padding: 0 }}>
                        <Suspense fallback={<Box p={2}>Loading editor...</Box>}>
                            <LazyEditor
                                options={{
                                    readOnly: disabled,
                                    minimap: { enabled: true },
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    formatOnPaste: true,
                                    formatOnType: true,
                                    folding: true,
                                    wordWrap: 'on',
                                    fontSize: 14,
                                    tabSize: 2,
                                    scrollbar: {
                                        vertical: 'auto',
                                        horizontal: 'auto'
                                    }
                                }}
                                height="100%"
                                language={editorMode}
                                value={fullscreenValue}
                                onChange={handleFullscreenChange}
                                theme={`custom-${monacoTheme}`}
                                beforeMount={(monaco) => {
                                    monaco.editor.defineTheme(`custom-${monacoTheme}`, {
                                        base: monacoTheme,
                                        inherit: true,
                                        rules: [],
                                        colors: {
                                            'editor.background': backgroundColor,
                                            'editorLineNumber.foreground': colorTextInactive,
                                            'editorLineNumber.activeForeground': colorText,
                                        }
                                    });

                                    if (editorMode === 'json') {
                                        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                                            validate: true,
                                            allowComments: false,
                                            schemas: [],
                                            schemaValidation: 'error',
                                            schemaRequest: 'error',
                                            trailingCommas: 'error',
                                            comments: 'error'
                                        });
                                    }
                                }}
                                onMount={(editor) => {
                                    editor.updateOptions({
                                        contextmenu: true,
                                        smoothScrolling: true,
                                        cursorBlinking: 'smooth',
                                        cursorSmoothCaretAnimation: true,
                                        renderWhitespace: 'selection',
                                        renderControlCharacters: false,
                                        fontLigatures: true,
                                        bracketPairColorization: {
                                            enabled: true,
                                            independentColorPoolPerBracketType: true
                                        }
                                    });
                                    
                                    // Focus the editor when fullscreen opens
                                    editor.focus();
                                }}
                            />
                        </Suspense>
                    </DialogContent>
                </Box>
            </Dialog>
        </Box>
    );
};